/* eslint-disable @nx/enforce-module-boundaries */
import { DIALOG_DATA } from "@angular/cdk/dialog";
import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { PopupDto, PopupService } from "@malirang/api-client";
import {
    fnGetKeyByValue,
    NO_SEE_TODAY_ADVERTISE_POPUP,
    NO_SEE_TODAY_NORMAL_POPUP,
    PopupType,
} from "@malirang/common";
import {
    LocalStorageService,
    ModalService,
    SwiperDirective,
    ToastService,
} from "@malirang/front-share";
import { ActionSheetModalService } from "apps/client/src/services/action-sheet-modal.service";
import dayjs from "dayjs";
import { PopupControllerGetPopupSettingByType$Params } from "libs/api-client/src/lib/fn/popup/popup-controller-get-popup-setting-by-type";
import { SwiperOptions } from "swiper/types";

@Component({
    selector: "app-popup-mobile",
    imports: [CommonModule, SwiperDirective],
    templateUrl: "./popup-mobile.component.html",
    styleUrls: ["./popup-mobile.component.scss"],
})
export default class PopupMobileComponent {
    readonly popupService = inject(PopupService);
    readonly toastService = inject(ToastService);
    readonly actionSheetModalService = inject(ActionSheetModalService);
    readonly localStorageService = inject(LocalStorageService);
    readonly dialogData = inject(DIALOG_DATA);
    readonly modalService = inject(ModalService);

    popupSetting$ = rxResource({
        stream: () =>
            this.popupService.popupControllerGetPopupSettingByType({
                type: this.dialogData.type as keyof typeof PopupType,
            } as PopupControllerGetPopupSettingByType$Params),
    });

    baseSwiperOptions: SwiperOptions = {
        slidesPerView: 1,
        centeredSlides: true,
        allowTouchMove: true,
        spaceBetween: 16,
        loop: false,
        autoplay: {
            delay: 5000,
        },
    };

    /**
     * @description 팝업
     */
    popups = signal<PopupDto[]>(this.dialogData.popups);

    /**
     * @description 팝업 인덱스
     */
    realIndex = signal<number>(0);

    /**
     * @description 팝업 스와이퍼 옵션
     */
    swiperOptions = signal<SwiperOptions>({});

    /**
     * @description 팝업 위치 조정 가능 여부 및 설정
     */
    adTop = signal<number | undefined>(undefined);
    adLeft = signal<number | undefined>(undefined);
    normalTop = signal<number | undefined>(undefined);
    normalLeft = signal<number | undefined>(undefined);

    constructor() {
        if (this.dialogData.popups && this.dialogData.popups?.length > 0) {
            if (
                !Capacitor.isNativePlatform() &&
                this?.popupSetting$?.value()?.isHandlePosition
            ) {
                this.adTop.set(this?.popupSetting$?.value()?.positionTop || 0);
                this.adLeft.set(
                    this?.popupSetting$?.value()?.positionLeft || 0,
                );
            }
            this.swiperOptions.set({
                ...this.baseSwiperOptions,
                autoplay: {
                    delay: this?.popupSetting$?.value()?.rollingSpeed || 5000,
                },
            });
        }
    }

    /**
     * @name handleNotToday
     * @description 팝업 오늘하루 그만보기
     * @param {keyof typeof PopupType} type
     * @param {Event} ev
     */
    async handleNotToday(
        type: keyof typeof PopupType,
        ev?: Event,
    ): Promise<void> {
        ev?.stopPropagation();
        ev?.preventDefault();

        const todayEnd = dayjs().endOf("day").toDate()?.toString();
        const key =
            type === fnGetKeyByValue(PopupType, PopupType.ADVERTISE)
                ? NO_SEE_TODAY_ADVERTISE_POPUP
                : NO_SEE_TODAY_NORMAL_POPUP;

        await this.localStorageService.set(key, todayEnd);
        try {
            if (Capacitor.isNativePlatform()) {
                await Preferences?.set({
                    key,
                    value: todayEnd,
                });
            }
        } catch (error) {
            console.error("Preferences set error:", error);
        }

        this.close();
        return;
    }

    /**
     * @name close
     * @description 팝업 닫기
     * @param {Event} ev
     * @returns {void}
     */
    close(ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();
        this.modalService.close();
    }
}
